import React, { useState } from 'react';
import { Box, Button, Drawer } from '@mui/material';
import { useDispatch } from 'react-redux';
import {
  RiRestaurantLine,
  RiImageLine,
  RiRectangleLine,
  RiCheckboxBlankCircleLine,
  RiShapeLine,
  RiPentagonLine,
  RiText,
} from '@remixicon/react';
import { addElement, saveToHistory } from '../../redux/slices/canvasSlice';
import MenuModal from './MenuModal';
import ImageModal from './ImageModal';

const TOOLBAR_WIDTH = 80;
const MODAL_WIDTH = 320;

/**
 * Left Toolbar - Vertical tool buttons
 * 
 * Tabs: Menu | Image | Rectangle | Circle | Triangle | Pentagon | Text
 * Active tab shows purple background (#6B22D5)
 * Menu/Image tabs open slide-in modals
 */
const LeftToolbar = ({ activeTab, onTabChange, storeId, apiToken, syncMenu, menuVersion }) => {
  const dispatch = useDispatch();

  const tools = [
    { id: 'menu', icon: RiRestaurantLine, label: 'Menu', hasModal: true },
    { id: 'image', icon: RiImageLine, label: 'Image', hasModal: true },
    { id: 'rectangle', icon: RiRectangleLine, label: 'Rectangle', hasModal: false },
    { id: 'circle', icon: RiCheckboxBlankCircleLine, label: 'Circle', hasModal: false },
    { id: 'triangle', icon: RiShapeLine, label: 'Triangle', hasModal: false },
    { id: 'pentagon', icon: RiPentagonLine, label: 'Pentagon', hasModal: false },
    { id: 'text', icon: RiText, label: 'Text', hasModal: false },
  ];

  const handleToolClick = (toolId) => {
    const tool = tools.find((t) => t.id === toolId);

    if (tool.hasModal) {
      // Toggle modal for Menu/Image
      onTabChange(activeTab === toolId ? null : toolId);
    } else {
      // Add element directly for shapes/text
      addElementToCanvas(toolId);
    }
  };

  const addElementToCanvas = (type) => {
    dispatch(saveToHistory()); // Save current state before adding
    
    switch (type) {
      case 'rectangle':
        dispatch(
          addElement({
            type: 'shape',
            properties: {
              shape: 'rectangle',
              backgroundColor: 'transparent',
              strokeColor: '#FFFFFF',
              strokeWidth: 1,
              strokeVisible: true,
              borderRadius: 8,
            },
            size: { width: 200, height: 150 },
          })
        );
        break;

      case 'circle':
        dispatch(
          addElement({
            type: 'shape',
            properties: {
              shape: 'circle',
              backgroundColor: 'transparent',
              strokeColor: '#FFFFFF',
              strokeWidth: 1,
              strokeVisible: true,
            },
            size: { width: 150, height: 150 },
          })
        );
        break;

      case 'triangle':
        dispatch(
          addElement({
            type: 'shape',
            properties: {
              shape: 'triangle',
              backgroundColor: 'transparent',
              strokeColor: '#FFFFFF',
              strokeWidth: 1,
              strokeVisible: true,
            },
            size: { width: 150, height: 150 },
          })
        );
        break;

      case 'pentagon':
        dispatch(
          addElement({
            type: 'shape',
            properties: {
              shape: 'pentagon',
              backgroundColor: 'transparent',
              strokeColor: '#FFFFFF',
              strokeWidth: 1,
              strokeVisible: true,
            },
            size: { width: 150, height: 150 },
          })
        );
        break;

      case 'text':
        dispatch(
          addElement({
            type: 'text',
            properties: {
              text: 'Double click to edit',
              fontSize: '24px',
              fontFamily: 'Outfit',
              color: '#000000',
              textAlign: 'left',
            },
          })
        );
        break;

      default:
        break;
    }
  };

  return (
    <>
      {/* Toolbar */}
      <Box
        sx={{
          width: TOOLBAR_WIDTH,
          backgroundColor: 'white',
          borderRight: '1px solid #E0E0E0',
          display: 'flex',
          flexDirection: 'column',
          padding: '12px 0',
          gap: 1,
        }}
      >
        {tools.map((tool) => {
          const Icon = tool.icon;
          const isActive = activeTab === tool.id;

          return (
            <Button
              key={tool.id}
              onClick={() => handleToolClick(tool.id)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                padding: '12px 8px',
                minWidth: 'auto',
                borderRadius: 1,
                margin: '0 8px',
                backgroundColor: isActive ? '#6B22D5' : 'transparent',
                color: isActive ? 'white' : '#4B4A57',
                '&:hover': {
                  backgroundColor: isActive ? '#5B1DB7' : '#F5F5F5',
                },
                transition: 'all 0.2s',
              }}
            >
              <Icon size={24} />
              <Box
                sx={{
                  fontSize: '10px',
                  fontWeight: 500,
                  textTransform: 'none',
                }}
              >
                {tool.label}
              </Box>
            </Button>
          );
        })}
      </Box>

      {/* Menu Modal */}
      <Drawer
        anchor="left"
        open={activeTab === 'menu'}
        onClose={() => onTabChange(null)}
        variant="persistent"
        sx={{
          '& .MuiDrawer-paper': {
            width: MODAL_WIDTH,
            left: TOOLBAR_WIDTH,
            top: 64, // Below header
            height: 'calc(100vh - 64px)',
            borderLeft: '1px solid #E0E0E0',
            boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
          },
        }}
      >
        <MenuModal
          onClose={() => onTabChange(null)}
          storeId={storeId}
          apiToken={apiToken}
          syncMenu={syncMenu}
          menuVersion={menuVersion}
        />
      </Drawer>

      {/* Image Modal */}
      <Drawer
        anchor="left"
        open={activeTab === 'image'}
        onClose={() => onTabChange(null)}
        variant="persistent"
        sx={{
          '& .MuiDrawer-paper': {
            width: MODAL_WIDTH,
            left: TOOLBAR_WIDTH,
            top: 64,
            height: 'calc(100vh - 64px)',
            borderLeft: '1px solid #E0E0E0',
            boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
          },
        }}
      >
        <ImageModal onClose={() => onTabChange(null)} />
      </Drawer>
    </>
  );
};

export default LeftToolbar;

